import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Navigate, Routes, Route, useNavigate, useParams, useMatch, useResolvedPath } from 'react-router-dom';
import Assessment from './assessment';
import UserFinishStep from './user-finish-step';
import { isProduction, querystring } from '../../utils/helpers';
import AssessmentsAnswerView from '../Assessments/AssessmentsAnswerView';
import { useStores } from '../../hooks/use-stores';
import { observer } from 'mobx-react';
import UserStartStep from './user-start-step';
import AdminStartStep from './admin-start-step';
import AdminFinishStep from './admin-finish-step';
import { Helmet } from 'react-helmet';
import Training from './training';
import { Container } from 'reactstrap';
import Waiting from '../../components/waiting';
import { Buffer } from 'buffer';
import { ThemedButton } from '@awarego-ui/components';

function DemoHome({ isAdmin, isTraining, isAssessment }) {
    const navigate = useNavigate();
    const { companyId: paramsCompanyId, reportTo: reportToBase64 } = useParams();
    const isRootMatch = useMatch('/:type/:companyId/:reportTo');
    const { demoStore, brandingStore } = useStores();
    const [inititalized, setInitialized] = useState();
    const {
        sessionId,
        companyId,
        demoUser,
        isAuthenticated,
        reportToName,
        reportToEmail,
    } = demoStore;
    const { brandingLoaded } = brandingStore;

    const resolved = useResolvedPath('.');
    const baseUrl = resolved.pathname;

    const restartDemo = useCallback(() => {
        demoStore.restart();
    }, [demoStore]);

    useEffect(() => {
        // ensure there is no session and demo user saved when you load the demo page
        if (isRootMatch && !inititalized) {
            restartDemo();
        }

        demoStore.setCompanyId(paramsCompanyId);
        if (reportToBase64) {
            try {
                let reportTo = Buffer.from(reportToBase64, 'base64').toString(
                    'utf8'
                );
                demoStore.setReportTo(reportTo);
            } catch (e) {
                console.error(e);
            }
        }
        setInitialized(true);
    }, [
        demoStore,
        paramsCompanyId,
        navigate,
        restartDemo,
        inititalized,
        companyId,
        isRootMatch,
        reportToBase64,
    ]);

    const userDemoLink = useMemo(() => {
        const currentPath = window.location.pathname;
        let path = currentPath.replace('try-demo', 'demo');
        return `${new URL(window.location).origin}${path}`;
    }, [reportToName, reportToEmail]);

    if (!inititalized || !brandingLoaded)
        return (
            <Container className="main-content">
                <Waiting waitingActive={true} from={'demo'} />
            </Container>
        );

    // Root render component
    const RootComponent = () => {
        if (isAuthenticated) {
            if (isAssessment)
                return <Navigate to="assessment" />;
            if (isTraining)
                return <Navigate to="training/subjects" />;
        }
        if (isAdmin)
            return (
                <AdminStartStep
                    copyLink={userDemoLink}
                    isAssessment={isAssessment}
                />
            );
        return (
            <UserStartStep
                isAssessment={isAssessment}
            />
        );
    };

    // Assessment Answers View
    const AssessmentAnswersComponent = () => {
        let questionIndex = querystring('index');
        return (
            <AssessmentsAnswerView
                parentUrl={baseUrl}
                companyId={companyId}
                assessmentId={sessionId}
                currentUser={demoUser}
                assessmentStore={demoStore}
                questionStore={demoStore}
                index={questionIndex}
                isDemo
            />
        );
    };

    // Training Finish Component
    const TrainingFinishComponent = () => {
        if (isAdmin)
            return (
                <AdminFinishStep
                    copyLink={userDemoLink}
                    isAssessment={false}
                />
            );
        return (
            <UserFinishStep
                isAssessment={false}
            />
        );
    };

    // Assessment Finish Component
    const AssessmentFinishComponent = () => {
        if (isAdmin)
            return (
                <AdminFinishStep
                    copyLink={userDemoLink}
                    isAssessment={isAssessment}
                />
            );
        return (
            <UserFinishStep
                isAssessment={isAssessment}
            />
        );
    };

    return (
        <>
            {/*debug button*/}
            {!isProduction && (
                <ThemedButton
                    onClick={restartDemo}
                    style={{
                        position: 'fixed',
                        top: 20,
                        right: 0,
                        zIndex: 10000,
                        background: 'red',
                    }}
                    text="restart"
                />
            )}
            <Helmet>
                <title>
                    {isAssessment
                        ? 'Human Risk Assessment'
                        : 'Security Awareness Training'}{' '}
                    demo
                </title>
            </Helmet>
            <Routes>
                <Route index element={<RootComponent />} />

                {/* Protected routes - redirect to root if not authenticated */}
                {!isAuthenticated ? (
                    <Route path="*" element={<Navigate to="" replace />} />
                ) : (
                    <>
                        <Route
                            path="assessment/answers"
                            element={<AssessmentAnswersComponent />}
                        />
                        <Route
                            path="assessment/give-feedback"
                            element={
                                <UserFinishStep
                                    isAssessment
                                    isFeedbackPage
                                />
                            }
                        />
                        <Route
                            path="training/give-feedback"
                            element={
                                <UserFinishStep
                                    isFeedbackPage
                                />
                            }
                        />
                        <Route
                            path="training/finish"
                            element={<TrainingFinishComponent />}
                        />
                        <Route
                            path="assessment/finish"
                            element={<AssessmentFinishComponent />}
                        />
                        <Route
                            path="assessment/*"
                            element={
                                <Assessment
                                    companyId={companyId}
                                    uniqueId={sessionId}
                                    assessmentId={sessionId}
                                    isAdmin={isAdmin}
                                />
                            }
                        />
                        <Route
                            path="training/*"
                            element={
                                <Training
                                    companyId={companyId}
                                    trainingId={sessionId}
                                    isAdmin={isAdmin}
                                />
                            }
                        />
                    </>
                )}
            </Routes>
        </>
    );
}

export default observer(DemoHome);
